import { GetTBSDetailsResEntity, TBSReceiverDetailsEntity } from "../interface/tbs-tracker/send-tbs";
import { IsNullOrUndefined } from "./null-check";

/**
 * Rules of the receiver form ("Verified By Receiver" on Verify TBS Items, "Receiver Details" on View TBS).
 *
 * A row is answered by the receiver only when there is something to verify:
 *  - the sender left it blank ("-" / empty)  -> nothing to enter, shows "-"
 *  - the sender answered "No"                -> nothing to enter, shows "-"
 * The quantity is always answered by the receiver (the received quantity).
 *
 * The receiver types an answer for thickness, quantity, width and height only: whole numbers (the quantity is the received
 * quantity, the three measurements are in MM). The other rows (type of TBS, category, brand name / code, select size, drawing)
 * show "-" in the receiver column. Records verified earlier can still hold answers for those rows (Yes / No, typed text,
 * Matched / Unmatched); they are still shown as they were saved.
 */

export type TBSVerifyAnswerKind = "yesNo" | "text" | "number";

/** longest typed category / brand name / brand code */
export const TBS_RECEIVER_TEXT_MAX_LENGTH = 100;
/** thickness / width / height are whole numbers from 1 to this (same as the sender's step 1) */
export const TBS_RECEIVER_MEASURE_MAX = 100000;

/**
 * "Type of TBS" (stored as `typeOfTbs`: skin / hardware) is hidden for now: every TBS is saved as a skin.
 * Set to true to bring back both the Step 1 choice and the Preview / Verify / View row.
 */
export const IS_TYPE_OF_TBS_VISIBLE = false;

/** The rows the receiver is asked to type an answer for (the quantity row is handled on its own). */
export const TBS_RECEIVER_ASKED_FIELDS: string[] = ["thickness", "width", "height"];

/** The rows that can carry an answer from the receiver, and what the answer is; older records hold answers for rows that are no longer asked. */
const TBS_VERIFY_ANSWER_KIND_BY_FIELD: Record<string, TBSVerifyAnswerKind> = {
    typeOfTbs: "yesNo",
    selectSize: "yesNo",
    anyDrawing: "yesNo",
    category: "text",
    brandName: "text",
    brandCode: "text",
    thickness: "number",
    width: "number",
    height: "number",
};

/** how a typed row is called in its messages ("Please enter thickness!") */
const TBS_RECEIVER_FIELD_NAME: Record<string, string> = {
    category: "category",
    brandName: "brand name",
    brandCode: "brand code",
    thickness: "thickness",
    width: "width",
    height: "height",
};

const isBlankSenderValue = (value: unknown): boolean => IsNullOrUndefined(value) || ["", "-"].includes(String(value).trim());

const isNoSenderValue = (value: unknown): boolean => String(value).trim().toLowerCase() === "no";

/** true when the receiver has nothing to verify on a row, because of what the sender filled in */
export const isVerificationNotRequired = (senderValue: unknown): boolean => isBlankSenderValue(senderValue) || isNoSenderValue(senderValue);

/** What a saved answer on this row is (Yes / No, a text or a number); null when the row never took one or the sender left it blank / "No". */
const getStoredAnswerKind = (field: string, senderValue: unknown): TBSVerifyAnswerKind | null => {
    const kind = TBS_VERIFY_ANSWER_KIND_BY_FIELD[field];
    if (!kind || isVerificationNotRequired(senderValue)) {
        return null;
    }
    return kind;
};

/** What the receiver has to type on this row now (a number); null when the row takes no answer from the receiver. */
export const getVerifyAnswerKind = (field: string, senderValue: unknown): TBSVerifyAnswerKind | null =>
    TBS_RECEIVER_ASKED_FIELDS.includes(field) ? getStoredAnswerKind(field, senderValue) : null;

/**
 * The message for a typed row (text or number), "" when the value is fine. Text takes any characters (only blank is refused),
 * numbers are whole numbers from 1 to 100000 like the quantity fields.
 */
export const getReceiverValueError = (field: string, kind: TBSVerifyAnswerKind | null, value: string): string => {
    const name = TBS_RECEIVER_FIELD_NAME[field] ?? field;
    if (kind === "text") {
        if (value.trim() === "") { return `Please enter ${name}!`; }
        return value.trim().length > TBS_RECEIVER_TEXT_MAX_LENGTH ? `The ${name} can be up to ${TBS_RECEIVER_TEXT_MAX_LENGTH} characters` : "";
    }
    if (kind === "number") {
        if (value.trim() === "") { return `Please enter ${name}!`; }
        if (!/^[1-9]\d*$/.test(value.trim())) { return `Please enter valid ${name}!`; }
        return Number(value) > TBS_RECEIVER_MEASURE_MAX ? `The ${name} should be from 1 to ${TBS_RECEIVER_MEASURE_MAX}` : "";
    }
    return "";
};

/** One of the six typed rows that is a measurement (shown in MM). */
export const isReceiverMeasureField = (field: string): boolean => ["thickness", "width", "height"].includes(field);


export interface TBSReceivedQtyInput {
    /** quantity typed by the receiver */
    receivedQty: number;
    /** quantity the sender sent ("Filled By Sender" > Qty) */
    senderQty: number;
    /** items the sender selected (ticked boxes on the form when it opens) */
    sentCount: number;
    /** items still ticked on the form */
    keptCount: number;
}

/**
 * The received quantity has to match the items the receiver keeps ticked whenever
 *  - it is LESS than the sender quantity: the items that did not arrive must be unticked (sender 7, received 3 -> untick 4), or
 *  - any item is unticked: the quantity is what the store can issue.
 * The full quantity with nothing unticked is free, as before.
 * Returns the message to show, or "" when the quantity is fine. Unticking every item is reported separately.
 */
export const getReceivedQtyError = ({ receivedQty, senderQty, sentCount, keptCount }: TBSReceivedQtyInput): string => {
    if (sentCount <= 0 || keptCount <= 0) {
        return "";
    }

    const returnedCount = sentCount - keptCount;
    if (receivedQty >= senderQty && returnedCount === 0) {
        return "";
    }
    if (receivedQty === keptCount) {
        return "";
    }

    if (keptCount > receivedQty) {
        const toUntick = keptCount - receivedQty;
        return `Quantity ${receivedQty} is less than the sender quantity ${senderQty}. Please untick ${toUntick} more ${toUntick === 1 ? "item that was" : "items that were"} not received.`;
    }
    return `Quantity should be ${keptCount}, as ${returnedCount} ${returnedCount === 1 ? "item is" : "items are"} returned.`;
};

/** rows whose label is not simply the field name split into words */
const TBS_DETAIL_LABEL_OVERRIDES: Record<string, string> = {
    typeOfTbs: "Type of TBS",
};

/** "typeOfTbs" -> "Type of TBS", "offCutRequired" -> "Off Cut Required" */
export const formatTbsDetailLabel = (key: string): string => TBS_DETAIL_LABEL_OVERRIDES[key]
    ?? key.replace(/([A-Z])/g, " $1").replace(/^./, (str: string) => str.toUpperCase());

/** The rows of the "Filled By Sender" table: model + dispatch values (thickness / width / height only when the sender entered them; "Type of TBS" only while it is visible). */
export const buildTbsDetailEntries = (data: GetTBSDetailsResEntity, isTypeOfTbsVisible: boolean = IS_TYPE_OF_TBS_VISIBLE): [string, string | number][] => {
    const { model, dispatch } = data;
    const details: Record<string, string | number> = {
        typeOfTbs: model.typeOfTbs,
        category: model.categoryName,
        brandName: model.brandName || "-",
        brandCode: model.brandCode || "-",
        thickness: !IsNullOrUndefined(model.thickness) ? `${model.thickness} MM` : "-",
        qty: model.qty,
        width: !IsNullOrUndefined(model.width) ? `${model.width} MM` : "-",
        height: !IsNullOrUndefined(model.height) ? `${model.height} MM` : "-",
        selectSize: dispatch.selectSize,
        anyDrawing: dispatch.anyDrawing ? "Yes" : "No",
        offCutRequired: dispatch.offCutRequired ? "Yes" : "No",
        dispatchFrom: dispatch.dispatchFrom,
        modeOfTransport: dispatch.modeOfTransport,
        concernPersonName: dispatch.concernPersonName,
        contactNumber: dispatch.contactNumber,
    };

    return Object.entries(details).filter(([key]) => {
        if (key === "typeOfTbs") { return isTypeOfTbsVisible; }
        if (key === "thickness") { return !IsNullOrUndefined(model.thickness); }
        if (key === "width") { return !IsNullOrUndefined(model.width); }
        if (key === "height") { return !IsNullOrUndefined(model.height); }
        return true;
    });
};

/** What the receiver gave for one row as plain text for an input ("" when nothing); an old matched / unmatched answer reads that way. */
export const getReceiverAnswerRaw = (field: string, receiver: TBSReceiverDetailsEntity): string => {
    if (field === "qty") {
        return IsNullOrUndefined(receiver.qty) ? "" : String(receiver.qty);
    }
    const answer = (receiver as unknown as Record<string, unknown>)[field];
    if (typeof answer === "boolean") { return TBS_VERIFY_ANSWER_KIND_BY_FIELD[field] === "yesNo" ? (answer ? "Yes" : "No") : (answer ? "Matched" : "Unmatched"); }
    if (typeof answer === "number") { return String(answer); }
    return typeof answer === "string" ? answer : "";
};

/** The receiver's answer for one row as text, exactly as the verify form shows it ("-" when nothing was verified). */
export const getReceiverAnswerText = (field: string, senderValue: unknown, receiver: TBSReceiverDetailsEntity): string => {
    if (field === "qty") {
        return IsNullOrUndefined(receiver.qty) ? "-" : String(receiver.qty);
    }

    const kind = getStoredAnswerKind(field, senderValue);
    if (!kind) {
        return "-";
    }

    const answer = (receiver as unknown as Record<string, unknown>)[field];
    if (kind === "yesNo") {
        return typeof answer === "boolean" ? (answer ? "Yes" : "No") : "-";
    }
    const raw = getReceiverAnswerRaw(field, receiver);
    if (raw === "") { return "-"; }
    return typeof answer === "number" && isReceiverMeasureField(field) ? `${raw} MM` : raw;
};

/**
 * Compares Send Quantity and Receive Quantity from a TBS record.
 * Converts string or numeric quantity values safely before comparison (e.g. "100" and 100 are treated as equal).
 * Returns true (GREEN) if Send Quantity === Receive Quantity, and false (RED) otherwise.
 */
export const isTbsQtyMatched = (
    sendQty: unknown,
    receiveQty: unknown
): boolean => {
    if (IsNullOrUndefined(sendQty) || IsNullOrUndefined(receiveQty)) {
        return false;
    }
    const sendStr = String(sendQty).trim();
    const receiveStr = String(receiveQty).trim();
    if (sendStr === "" || receiveStr === "") {
        return false;
    }
    const sendNum = Number(sendStr);
    const receiveNum = Number(receiveStr);
    if (isNaN(sendNum) || isNaN(receiveNum)) {
        return false;
    }
    return sendNum === receiveNum;
};

/**
 * Color of the "Receiver Details" icon of the TBS lists: true (GREEN) only when the receiver reported nothing against what the sender sent.
 * Red when the quantity differs OR the server flagged any other change: a typed thickness / width / height that is not the sender's value,
 * or items returned (at verification or afterwards).
 */
export const isReceiverReportMatched = (
    sendQty: unknown,
    receiveQty: unknown,
    hasReceiverChange?: boolean
): boolean => isTbsQtyMatched(sendQty, receiveQty) && !hasReceiverChange;
